import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

// Types
export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  variant?: "default" | "pills" | "underline";
  orientation?: "horizontal" | "vertical";
}

export interface TabPanelControlProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

export interface TabPanelTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface TabPanelContentProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  value: string;
}

// Context
const TabPanelContext = React.createContext<{
  value: string;
  onValueChange: (value: string) => void;
  variant: "default" | "pills" | "underline";
  name: string;
} | null>(null);

// Hook
const useTabPanel = () => {
  const context = React.useContext(TabPanelContext);
  if (!context) {
    throw new Error("TabPanel components must be used within a TabPanel");
  }
  return context;
};

// Main Component - Root container that manages both tabs and content
const TabPanel = React.forwardRef<HTMLDivElement, TabPanelProps>(
  (
    {
      className,
      asChild = false,
      value,
      onValueChange,
      defaultValue,
      variant = "default",
      orientation = "horizontal",
      children,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(
      defaultValue || ""
    );
    const controlledValue = value !== undefined ? value : internalValue;
    const handleValueChange = (newValue: string) => {
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    };

    // Ensure we have a valid initial value
    React.useEffect(() => {
      if (
        defaultValue !== undefined &&
        defaultValue !== "" &&
        internalValue === ""
      ) {
        setInternalValue(defaultValue);
      }
    }, [defaultValue, internalValue]);

    const Comp = asChild ? Slot : "div";

    // Generate a unique name for radio button grouping
    const tabPanelName = React.useMemo(
      () => `tab-panel-${Math.random().toString(36).substr(2, 9)}`,
      []
    );

    return (
      <TabPanelContext.Provider
        value={{
          value: controlledValue,
          onValueChange: handleValueChange,
          variant,
          name: tabPanelName,
        }}
      >
        <Comp
          ref={ref}
          data-slot="tab-panel"
          className={cn("wl-tab-panel-root", className)}
          {...props}
        >
          {children}
        </Comp>
      </TabPanelContext.Provider>
    );
  }
);

// Tab Control Component - Uses TabBar internally for the header
const TabPanelControl = React.forwardRef<HTMLDivElement, TabPanelControlProps>(
  (
    {
      className,
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        data-slot="tab-panel-control"
        className={cn("wl-tab-panel-control", className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

// Tab Trigger Component - Individual tab button
const TabPanelTrigger = React.forwardRef<HTMLDivElement, TabPanelTriggerProps>(
  (
    {
      className,
      asChild = false,
      value,
      label,
      icon,
      disabled = false,
      children,
      ...props
    },
    ref
  ) => {
    const { value: selectedValue, onValueChange, variant, name } = useTabPanel();
    const isSelected = selectedValue === value;
    const isDisabled = disabled;

    const handleClick = () => {
      if (!isDisabled) {
        onValueChange(value);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    };

    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        data-slot="tab-panel-trigger"
        role="tab"
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        className={cn(
          "wl-tabs-bar",
          isSelected && "wl-tabs-bar-selected",
          isDisabled && "wl-tabs-bar-disabled",
          className
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
        {...props}
      >
        <input
          id={`${name}-${value}`}
          aria-label={label}
          type="radio"
          name={name}
          checked={isSelected}
          disabled={isDisabled}
          readOnly
          className="wl-input-radio"
        />
        <label
          htmlFor={`${name}-${value}`}
          className="wl-d-inline-flex wl-align-items-center wl-gap-1"
        >
          {icon && <span className="wl-tabs-icon">{icon}</span>}
          {label}
        </label>
        <div className="wl-w-100">
          <div
            className={cn("wl-tabs-line", isSelected && "wl-tabs-line-visible")}
          />
        </div>
      </Comp>
    );
  }
);

// Tab Content Component - Content area for each tab
const TabPanelContent = React.forwardRef<HTMLDivElement, TabPanelContentProps>(
  (
    {
      className,
      asChild = false,
      value,
      children,
      ...props
    },
    ref
  ) => {
    const { value: selectedValue } = useTabPanel();
    const isActive = selectedValue === value;

    if (!isActive) {
      return null;
    }

    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        data-slot="tab-panel-content"
        role="tabpanel"
        data-state={isActive ? "active" : "inactive"}
        className={cn("wl-tab-panel-content", className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

TabPanel.displayName = "TabPanel";
TabPanelControl.displayName = "TabPanelControl";
TabPanelTrigger.displayName = "TabPanelTrigger";
TabPanelContent.displayName = "TabPanelContent";

export { TabPanel, TabPanelControl, TabPanelTrigger, TabPanelContent };
